import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import BottomNav from '../components/BottomNav';
import { 
  ArrowLeft, 
  Clock, 
  Mic, 
  Calendar, 
  FolderOpen, 
  Settings, 
  Download,
  Zap,
  PlayCircle,
  HelpCircle,
  ChevronRight,
  Sparkles,
  FileText
} from 'lucide-react';

const Docs = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeSection, setActiveSection] = useState('getting-started');

  const sections = [
    { id: 'getting-started', label: 'Getting Started', icon: Sparkles },
    { id: 'voice-logging', label: 'Voice Logging', icon: Mic },
    { id: 'notes', label: 'Voice Notes', icon: FileText },
    { id: 'timers', label: 'Timer Feature', icon: PlayCircle },
    { id: 'projects', label: 'Projects & Budgets', icon: FolderOpen },
    { id: 'calendar', label: 'Calendar & To-Dos', icon: Calendar },
    { id: 'export', label: 'Export Data', icon: Download },
    { id: 'shortcuts', label: 'Keyboard Shortcuts', icon: Zap },
    { id: 'faq', label: 'FAQ', icon: HelpCircle }
  ];

  const scrollToSection = (id) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleBack = () => {
    // Always go back to landing page for docs
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-blue-50 to-teal-50 dark:from-slate-900 dark:to-slate-800 pb-20 transition-colors">
      {/* Header */}
      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-md shadow-md sticky top-0 z-10 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-gray-700 dark:text-gray-300 hover:text-cyan-600 dark:hover:text-cyan-400 transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Back to Home</span>
            </button>
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-cyan-600 dark:text-cyan-400 transition-colors" />
              <h1 className="text-2xl font-bold bg-gradient-to-r from-cyan-600 to-blue-600 dark:from-cyan-400 dark:to-blue-400 bg-clip-text text-transparent transition-colors" style={{ fontFamily: 'Inter, sans-serif' }}>
                Logly Documentation
              </h1>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Sidebar Navigation */}
          <div className="lg:col-span-1">
            <Card className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 sticky top-24 transition-colors">
              <div className="p-4">
                <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3 transition-colors">Contents</h3>
                <nav className="space-y-1">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    return (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                          activeSection === section.id
                            ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm font-medium">{section.label}</span>
                      </button>
                    );
                  })}
                </nav>
              </div>
            </Card>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Getting Started */}
            <Card id="getting-started" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-cyan-500 to-blue-600 dark:from-cyan-600 dark:to-blue-500 rounded-lg transition-colors">
                    <Sparkles className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Getting Started</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-lg">
                    <strong>Welcome to Logly!</strong> Track your time effortlessly with voice commands and smart timers.
                  </p>
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-4 rounded-lg border border-cyan-200 dark:border-cyan-800 transition-colors">
                    <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100 transition-colors">🎉 Your Free Trial</h4>
                    <p className="text-gray-800 dark:text-gray-200 transition-colors">You have a <strong>7-day free trial</strong> to explore all features. After that, it's just <strong>$2.99/month</strong>.</p>
                  </div>
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Quick Start:</h4>
                    <ol className="list-decimal list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                      <li>Log your first time entry using the microphone button</li>
                      <li>Create projects to organize your work</li>
                      <li>Start timers for real-time tracking</li>
                      <li>Export your data for billing and reporting</li>
                    </ol>
                  </div>
                </div>
              </div>
            </Card>

            {/* Voice Logging */}
            <Card id="voice-logging" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-600 dark:from-purple-600 dark:to-pink-500 rounded-lg transition-colors">
                    <Mic className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Voice Logging</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>Log time with your voice in 5 simple steps:</strong></p>
                  <ol className="list-decimal list-inside space-y-2 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                    <li><strong>Tap the microphone button</strong> on the home page</li>
                    <li><strong>Speak naturally:</strong> <em>"Worked 2 hours on client meeting with John"</em></li>
                    <li><strong>Tap Stop</strong> when done recording</li>
                    <li><strong>AI processes</strong> your speech and fills in the details</li>
                    <li><strong>Review and confirm</strong> the entry</li>
                  </ol>
                  
                  <div className="bg-cyan-50 dark:bg-cyan-900/20 p-4 rounded-lg border-l-4 border-cyan-500 dark:border-cyan-400 transition-colors">
                    <h4 className="font-semibold text-cyan-700 dark:text-cyan-300 mb-2 transition-colors">💡 Pro Tips:</h4>
                    <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      <li>✓ Speak clearly and include project name, duration, and task details</li>
                      <li>✓ Mention attendees or meeting participants for better tracking</li>
                      <li>✓ AI learns from your patterns and gets smarter over time</li>
                      <li>✓ You can edit entries after creation if AI misunderstands something</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Example Voice Commands:</h4>
                    <div className="space-y-2">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <p className="text-sm italic text-gray-800 dark:text-gray-200 transition-colors">"Spent 3 hours on Project Alpha coding"</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <p className="text-sm italic text-gray-800 dark:text-gray-200 transition-colors">"Meeting with Sarah about design for 1.5 hours"</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <p className="text-sm italic text-gray-800 dark:text-gray-200 transition-colors">"Worked on client presentation from 9 to 11 AM"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Voice Notes */}
            <Card id="notes" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-500 rounded-lg transition-colors">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Voice Notes with AI Summaries</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>Record voice notes with AI-powered transcription and summarization:</strong></p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Creating Voice Notes:</h4>
                    <ol className="list-decimal list-inside space-y-2 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                      <li>Navigate to <strong>Notes</strong> tab in bottom navigation</li>
                      <li>Click <strong>"New Note"</strong> button</li>
                      <li>Select meeting date (Today, Yesterday, or custom date - max 30 days ago)</li>
                      <li>Optionally add time and link to a project</li>
                      <li>Click <strong>"Tap to Start Recording"</strong> and speak (max 5 minutes)</li>
                      <li>Click <strong>"Stop Recording"</strong> when done</li>
                      <li>AI transcribes your speech and generates a summary</li>
                      <li>Review transcript and AI summary</li>
                      <li>Optionally create a time entry linked to the note</li>
                      <li>Click <strong>"Save Note"</strong> to save</li>
                    </ol>
                  </div>

                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border-l-4 border-blue-500 dark:border-blue-400 transition-colors">
                    <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2 transition-colors">✨ AI Features:</h4>
                    <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      <li>✓ <strong>Automatic Transcription:</strong> Uses OpenAI Whisper to convert speech to text</li>
                      <li>✓ <strong>AI Summarization:</strong> GPT-3.5 generates 3-5 bullet points with key decisions and action items</li>
                      <li>✓ <strong>Smart Title Generation:</strong> Auto-generates titles if not provided (e.g., "Meeting Note - Nov 12, 2025")</li>
                      <li>✓ <strong>Meeting Date Tracking:</strong> Track when meeting actually happened (can be different from recording date)</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Note Management:</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                      <li><strong>View Details:</strong> Click "View Details" to see full transcript and summary</li>
                      <li><strong>Export Notes:</strong> Download as text file or copy to clipboard</li>
                      <li><strong>Link to Time Entry:</strong> Create time entry linked to note for time tracking</li>
                      <li><strong>Search & Filter:</strong> Search notes by content, filter by project, date range, or time entry status</li>
                      <li><strong>Delete Notes:</strong> Discard notes you don't want to keep</li>
                    </ul>
                  </div>

                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-4 rounded-lg border-l-4 border-cyan-500 dark:border-cyan-400 transition-colors">
                    <h4 className="font-semibold text-cyan-700 dark:text-cyan-300 mb-2 transition-colors">💡 Pro Tips:</h4>
                    <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      <li>✓ Speak clearly about what happened in your meeting for better transcription</li>
                      <li>✓ Include key decisions, action items, and attendees in your notes</li>
                      <li>✓ Link notes to time entries to track meeting time</li>
                      <li>✓ Use search to quickly find notes by content or project</li>
                      <li>✓ Export notes for sharing or documentation</li>
                    </ul>
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Note Features:</h4>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <h5 className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">📅 Date & Time</h5>
                        <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Track when meeting actually happened (can be backdated up to 30 days)</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <h5 className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">🔗 Project Linking</h5>
                        <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Link notes to projects for better organization</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <h5 className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">⏱️ Time Entry Integration</h5>
                        <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Create time entries directly from notes</p>
                      </div>
                      <div className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                        <h5 className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">🔍 Search & Filter</h5>
                        <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Search notes by content, filter by project or date</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Timer Feature */}
            <Card id="timers" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-green-500 to-emerald-600 dark:from-green-600 dark:to-emerald-500 rounded-lg transition-colors">
                    <PlayCircle className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Timer Feature</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>Track time in real-time with desktop timers:</strong></p>
                  
                  <div className="space-y-3">
                    <div>
                      <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100 transition-colors">🚀 Quick Start Timers</h4>
                      <p className="text-gray-800 dark:text-gray-200 transition-colors">Click any Quick Start Timer card to instantly begin tracking your most common projects.</p>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100 transition-colors">📝 Detailed Timers</h4>
                      <ol className="list-decimal list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                        <li>Click "+ Start timer with description"</li>
                        <li>Select project and add task details</li>
                        <li>Timer appears in top-right corner</li>
                        <li>Click <strong>Pause</strong> to take breaks</li>
                        <li>Click <strong>Stop</strong> and add description to save</li>
                      </ol>
                    </div>
                  </div>

                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border-l-4 border-green-500 dark:border-green-400 transition-colors">
                    <h4 className="font-semibold text-green-700 dark:text-green-300 mb-2 transition-colors">⏱️ Timer Features:</h4>
                    <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      <li>✓ Runs in real-time and counts every second</li>
                      <li>✓ Pause/Resume functionality for breaks</li>
                      <li>✓ Timer widget visible on all pages</li>
                      <li>✓ Save with detailed notes and descriptions</li>
                      <li>✓ Discard option if you started by mistake</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            {/* Projects */}
            <Card id="projects" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-orange-500 to-red-600 dark:from-orange-600 dark:to-red-500 rounded-lg transition-colors">
                    <FolderOpen className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Advanced Project Management</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>Comprehensive project tracking with budgets, rates, timelines, and organization:</strong></p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Creating Projects:</h4>
                    <ol className="list-decimal list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                      <li>Go to <strong>Projects</strong> tab in bottom navigation</li>
                      <li>Click "+ New Project"</li>
                      <li>Enter project name and description</li>
                      <li>Choose a color for easy identification</li>
                      <li>Configure advanced features (budgets, rates, timelines, tags)</li>
                    </ol>
                  </div>

                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-4 rounded-lg border-l-4 border-cyan-500 dark:border-cyan-400 mt-4 transition-colors">
                    <h4 className="font-semibold text-cyan-700 dark:text-cyan-300 mb-3 transition-colors">📊 Phase 1: Project Budgets & Hourly Rates</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Time & Money Budgets:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>Set time budgets (hours) and/or money budgets per project</li>
                          <li>Choose between Fixed (hard limit) or Flexible (allows overage)</li>
                          <li>Auto-tracking: System updates spent amounts as you log time</li>
                          <li>Visual progress bars show budget consumption</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Budget Alerts (3 Levels):</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>🟢 75% - INFO: You are on track, getting close</li>
                          <li>🟡 90% - WARNING: Budget almost exhausted</li>
                          <li>🔴 100%+ - DANGER: Over budget!</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Hourly Rates:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>Set custom hourly rate per project</li>
                          <li>Multi-currency: USD, EUR, GBP, JPY, AUD, CAD, CHF, CNY, INR, SGD</li>
                          <li>Auto-calculate earnings based on logged hours</li>
                          <li>Mark as billable or non-billable</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-4 rounded-lg border-l-4 border-purple-500 dark:border-purple-400 mt-4 transition-colors">
                    <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-3 transition-colors">📅 Phase 2: Project Timelines & Deadline Alerts</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Timeline Management:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>Set start date and due date for each project</li>
                          <li>System automatically calculates days until due</li>
                          <li>Visual timeline bars on project cards</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-1">Deadline Alerts (Color-Coded):</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm">
                          <li>🔴 Red - Overdue: Project past due date</li>
                          <li>🟠 Orange - Critical: Due in 0-3 days</li>
                          <li>🟡 Yellow - Watch: Due in 4-7 days</li>
                          <li>🟢 Green - On Track: 8+ days remaining</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Status Indicators:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>✓ On Track: Budget and deadline healthy</li>
                          <li>⚠️ Watch: 75-89% budget used</li>
                          <li>🚨 At Risk: 90-99% budget or approaching deadline</li>
                          <li>🔴 Critical: 100%+ budget or overdue</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-4 rounded-lg border-l-4 border-green-500 dark:border-green-400 mt-4 transition-colors">
                    <h4 className="font-semibold text-green-700 dark:text-green-300 mb-3 transition-colors">📚 Phase 3: Archive, Tags & Organization</h4>
                    
                    <div className="space-y-3">
                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Archive Completed Projects:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>Move completed projects to Archive tab</li>
                          <li>Keeps workspace clean and focused</li>
                          <li>Archived projects retain all data</li>
                          <li>Restore anytime if needed</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Tags & Categories:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>Tags: Add multiple keywords (e.g., client, urgent)</li>
                          <li>Categories: Organize into groups (Client Work, Internal)</li>
                          <li>Filter projects by tags and categories</li>
                        </ul>
                      </div>

                      <div>
                        <p className="font-medium mb-1 text-gray-900 dark:text-gray-100 transition-colors">Default Project:</p>
                        <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                          <li>Set one project as default for quick logging</li>
                          <li>Voice entries and timers auto-assign to default</li>
                          <li>Change default anytime</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <div className="bg-orange-50 dark:bg-orange-900/20 p-4 rounded-lg border-l-4 border-orange-500 dark:border-orange-400 mt-4 transition-colors">
                    <h4 className="font-semibold text-orange-700 dark:text-orange-300 mb-2 transition-colors">✨ Enhanced Dashboard</h4>
                    <ul className="list-disc list-inside space-y-1 ml-4 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      <li>View comprehensive metrics (hours, earnings, active projects)</li>
                      <li>Week-over-week trend comparison</li>
                      <li>Project cards with budget progress and alerts</li>
                      <li>Click project cards for detailed modal view</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            {/* Calendar View */}
            <Card id="calendar" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-blue-500 to-indigo-600 dark:from-blue-600 dark:to-indigo-500 rounded-lg transition-colors">
                    <Calendar className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Calendar View & To-Do List</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  
                  {/* Calendar View */}
                  <div>
                    <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>See your week at a glance:</strong></p>
                    
                    <div className="space-y-2 mt-3">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Using the Calendar:</h4>
                      <ul className="list-disc list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                        <li>Navigate to <strong>Calendar</strong> tab</li>
                        <li>View hours blocked by day and time</li>
                        <li>Color-coded entries match project colors</li>
                        <li>Entries dynamically expand based on duration</li>
                        <li>Click any time entry to view/edit or quick delete</li>
                        <li>Swipe left/right to change weeks</li>
                        <li>See weekly totals at a glance</li>
                      </ul>
                    </div>

                    <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mt-3 border border-blue-200 dark:border-blue-800 transition-colors">
                      <h4 className="font-semibold text-blue-700 dark:text-blue-300 mb-2 transition-colors">📊 Calendar Benefits:</h4>
                      <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                        <li>✓ Visual overview of your time allocation</li>
                        <li>✓ Identify busy days and free slots</li>
                        <li>✓ Track work-life balance patterns</li>
                        <li>✓ Perfect for planning and scheduling</li>
                        <li>✓ Glassy iOS-style design for smooth UX</li>
                      </ul>
                    </div>
                  </div>

                  {/* To-Do List */}
                  <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 transition-colors">
                    <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-gray-100 transition-colors">Weekly To-Do List</h3>
                    <p className="text-gray-800 dark:text-gray-200 transition-colors"><strong>Plan and track tasks across 5 weeks:</strong></p>
                    
                    <div className="space-y-2 mt-3">
                      <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">Creating To-Dos:</h4>
                      <ol className="list-decimal list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                        <li>Open Calendar tab and scroll to To-Do panel</li>
                        <li>Select target week</li>
                        <li>Click "+ Add Task"</li>
                        <li>Fill in task details (title, description, due date)</li>
                        <li>Set priority level (High, Normal, Low)</li>
                        <li>Choose repeat setting (None, Daily, Weekly, Monthly)</li>
                        <li>Save and track completion</li>
                      </ol>
                    </div>

                    <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg mt-3 border border-purple-200 dark:border-purple-800 transition-colors">
                      <h4 className="font-semibold text-purple-700 dark:text-purple-300 mb-2 transition-colors">📝 To-Do Features:</h4>
                      <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                        <li>✓ <strong>Due Date Alerts:</strong> Overdue (red), Due Today (orange), Due Soon (yellow)</li>
                        <li>✓ <strong>Priority Levels:</strong> High (red), Normal (blue), Low (gray)</li>
                        <li>✓ <strong>Repeat Options:</strong> Daily, Weekly, Monthly recurring tasks</li>
                        <li>✓ <strong>Mark Complete:</strong> Check off completed tasks</li>
                        <li>✓ <strong>Dashboard Summary:</strong> Upcoming to-dos appear on Home page</li>
                        <li>✓ <strong>Smart Sorting:</strong> Tasks sorted by due date priority</li>
                      </ul>
                    </div>

                    <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-4 rounded-lg mt-3 border-l-4 border-cyan-500 dark:border-cyan-400 transition-colors">
                      <h4 className="font-semibold text-cyan-700 dark:text-cyan-300 mb-2 transition-colors">💡 Pro Tip:</h4>
                      <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Check your Home page dashboard for a quick summary of upcoming to-dos with due date notifications. Never miss a deadline!</p>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* Export Data */}
            <Card id="export" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-teal-500 to-cyan-600 dark:from-teal-600 dark:to-cyan-500 rounded-lg transition-colors">
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Export Data</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>Generate timesheets for billing and reporting:</strong></p>
                  
                  <div className="space-y-2">
                    <h4 className="font-semibold text-gray-900 dark:text-gray-100 transition-colors">How to Export:</h4>
                    <ol className="list-decimal list-inside space-y-1 ml-4 text-gray-800 dark:text-gray-200 transition-colors">
                      <li>Go to <strong>Settings</strong> → <strong>Export Data</strong></li>
                      <li>Select date range (start and end date)</li>
                      <li>Choose projects to include</li>
                      <li>Select format: <strong>CSV</strong> or <strong>PDF</strong></li>
                      <li>Click <strong>Export</strong></li>
                    </ol>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100 transition-colors">📄 CSV Format</h4>
                      <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Perfect for spreadsheets, accounting software, and data analysis. Import into Excel, Google Sheets, or QuickBooks.</p>
                    </div>
                    <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <h4 className="font-semibold mb-2 text-gray-900 dark:text-gray-100 transition-colors">📑 PDF Format</h4>
                      <p className="text-sm text-gray-800 dark:text-gray-200 transition-colors">Professional timesheets ready for client invoices. Includes project details, hours, and formatted for printing.</p>
                    </div>
                  </div>

                  <div className="bg-teal-50 dark:bg-teal-900/20 p-4 rounded-lg border-l-4 border-teal-500 dark:border-teal-400 transition-colors">
                    <h4 className="font-semibold text-teal-700 dark:text-teal-300 mb-2 transition-colors">💼 Perfect For:</h4>
                    <ul className="space-y-1 text-sm text-gray-800 dark:text-gray-200 transition-colors">
                      <li>✓ Client invoicing and billing</li>
                      <li>✓ Payroll and timesheet submission</li>
                      <li>✓ Project budget tracking</li>
                      <li>✓ Tax records and documentation</li>
                    </ul>
                  </div>
                </div>
              </div>
            </Card>

            {/* Keyboard Shortcuts */}
            <Card id="shortcuts" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-yellow-500 to-orange-600 dark:from-yellow-600 dark:to-orange-500 rounded-lg transition-colors">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Keyboard Shortcuts</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <p className="text-gray-900 dark:text-gray-100 transition-colors"><strong>Work faster with keyboard shortcuts:</strong></p>
                  
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <PlayCircle className="w-5 h-5 text-gray-600 dark:text-gray-400 transition-colors" />
                        <span className="text-gray-900 dark:text-gray-100 transition-colors">Start/Stop current timer</span>
                      </div>
                      <kbd className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg border-2 border-gray-300 dark:border-gray-600 font-mono font-semibold transition-colors">Space</kbd>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <Clock className="w-5 h-5 text-gray-600 dark:text-gray-400 transition-colors" />
                        <span className="text-gray-900 dark:text-gray-100 transition-colors">New timer</span>
                      </div>
                      <kbd className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg border-2 border-gray-300 dark:border-gray-600 font-mono font-semibold transition-colors">N</kbd>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <Settings className="w-5 h-5 text-gray-600 dark:text-gray-400 transition-colors" />
                        <span className="text-gray-900 dark:text-gray-100 transition-colors">Go to Settings</span>
                      </div>
                      <kbd className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg border-2 border-gray-300 dark:border-gray-600 font-mono font-semibold transition-colors">S</kbd>
                    </div>
                    
                    <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600 transition-colors">
                      <div className="flex items-center gap-3">
                        <span className="w-5 h-5 text-gray-600 dark:text-gray-400 transition-colors">✕</span>
                        <span className="text-gray-900 dark:text-gray-100 transition-colors">Close dialogs</span>
                      </div>
                      <kbd className="px-4 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 rounded-lg border-2 border-gray-300 dark:border-gray-600 font-mono font-semibold transition-colors">Esc</kbd>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            {/* FAQ */}
            <Card id="faq" className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm shadow-lg border-0 dark:border-gray-700 transition-colors">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="p-2 bg-gradient-to-br from-pink-500 to-rose-600 dark:from-pink-600 dark:to-rose-500 rounded-lg transition-colors">
                    <HelpCircle className="w-6 h-6 text-white" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100 transition-colors">Frequently Asked Questions</h2>
                </div>
                <div className="space-y-4 text-gray-700 dark:text-gray-300 transition-colors">
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 transition-colors">
                        <ChevronRight className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                        How accurate is voice recognition?
                      </h4>
                      <p className="ml-7 text-gray-600 dark:text-gray-400 transition-colors">Logly uses advanced AI to understand natural speech with high accuracy. The more you use it, the better it gets at understanding your patterns and terminology.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 transition-colors">
                        <ChevronRight className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                        Can I edit entries after creation?
                      </h4>
                      <p className="ml-7 text-gray-600 dark:text-gray-400 transition-colors">Yes! Click any time entry to edit the project, duration, date, notes, or attendees. Changes are saved automatically.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 transition-colors">
                        <ChevronRight className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                        What happens after my free trial?
                      </h4>
                      <p className="ml-7 text-gray-600 dark:text-gray-400 transition-colors">After your 7-day trial, you'll be asked to subscribe for $2.99/month. You can cancel anytime, and you'll retain access until the end of your billing period.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 transition-colors">
                        <ChevronRight className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                        Can I use Logly offline?
                      </h4>
                      <p className="ml-7 text-gray-600 dark:text-gray-400 transition-colors">Logly is a Progressive Web App (PWA). You can install it on your device for quick access. However, voice recognition and sync require an internet connection.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 transition-colors">
                        <ChevronRight className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                        How do I cancel my subscription?
                      </h4>
                      <p className="ml-7 text-gray-600 dark:text-gray-400 transition-colors">Go to Settings → Subscription section → Click "Manage Subscription". This opens Stripe Customer Portal where you can cancel or update your subscription.</p>
                    </div>

                    <div>
                      <h4 className="font-semibold text-lg mb-2 flex items-center gap-2 text-gray-900 dark:text-gray-100 transition-colors">
                        <ChevronRight className="w-5 h-5 text-cyan-600 dark:text-cyan-400 transition-colors" />
                        Is my data secure?
                      </h4>
                      <p className="ml-7 text-gray-600 dark:text-gray-400 transition-colors">Yes! All data is encrypted in transit and at rest. We use industry-standard security practices and never share your data with third parties.</p>
                    </div>
                  </div>

                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 dark:from-cyan-900/20 dark:to-blue-900/20 p-4 rounded-lg mt-6 border border-cyan-200 dark:border-cyan-800 transition-colors">
                    <h4 className="font-semibold text-cyan-700 dark:text-cyan-300 mb-2 transition-colors">Still have questions?</h4>
                    <p className="text-sm mb-3 text-gray-800 dark:text-gray-200 transition-colors">We're here to help!</p>
                    <Button
                      onClick={() => window.location.href = 'mailto:support@logly.work'}
                      className="bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white"
                    >
                      Contact Support
                    </Button>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </div>

      <BottomNav active="" />
    </div>
  );
};

export default Docs;
